import React, { useState } from 'react';
import useLang from '../hooks/useLang';
import Impressum from './Impressum';
import AGBDatenschutz from './AGBDatenschutz';
import FAQ from './FAQ';
import EarlyAccessBanner from './EarlyAccessBanner';
import DurumWidget from './DurumWidget';

const ImprovedHomePageClean = ({ onStart, onShowAboutUs, onShowAmpelTest }) => {
  const { lang } = useLang();
  const [showImpressum, setShowImpressum] = useState(false);
  const [showAGBDatenschutz, setShowAGBDatenschutz] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const isFreeMode = (process.env.REACT_APP_FREE_MODE === 'true') || (urlParams.get('free') === 'true');
  // Hide Durum widgets for public; allow manual opt-in via ?durum=true
  const showDurum = urlParams.get('durum') === 'true';
  
  // Sprachabhängige Inhalte - vereinfacht
  const content = {
    de: {
      heroTitle: "EU-Förderungen finden",
      heroSubtitle: "In 3 Minuten",
      heroDescription: "Wir durchsuchen alle verfügbaren EU-Förderprogramme und finden die passenden für Ihren Betrieb.",
      priceText: isFreeMode ? "Vorübergehend kostenlos" : "9,90€ einmalig",
      ctaButton: isFreeMode ? "Kostenlos starten" : "Jetzt starten",
      trustLine: "Von Landwirten für Landwirte entwickelt • Über 555 Programme • 30 Tage Zufriedenheitsgarantie",
      
      // Benefits Features Content
      benefitsTitle: "🚜 Endlich alle Förderungen im Blick",
      benefit1: "Von 5.000€ Kleinförderung bis 2 Mio€ Großprojekt",
      benefit2: "Auch \"versteckte\" Programme in Energie & Innovation",
      benefit3: "Perfekt für Hofnachfolge, Bio-Umstellung, Digitalisierung",
      benefit4: "549 Programme analysiert",
      benefit5: "6 Länder abgedeckt",
      benefit6: "30 Tage Geld-zurück-Garantie",
      
      // Über Uns Content
      aboutTitle: "Über Uns",
      aboutSubtitle: "Unsere Wurzeln, Ihre Zukunft: Die Geschichte hinter Centeo",
      missionTitle: "Unsere Mission: Klarheit im Förderdschungel",
      missionText: "Die Agrarförderung in der EU ist ein Labyrinth aus Anträgen, sich ändernden Vorschriften und unzähligen Programmen. Wir haben Centeo gegründet, weil wir glauben, dass jeder Landwirt – unabhängig von der Betriebsgröße oder dem technischen Know-how – einen fairen und einfachen Zugang zu der Unterstützung verdient, die ihm zusteht.",
      originTitle: "Unsere Herkunft: Landwirtschaft im Herzen, Daten im Kopf",
      originText: "Hinter Centeo steht eine persönliche Geschichte. Unser Gründer, Arsenio Longo, entstammt einer Familie, in der seit Generationen Landwirtschaft betrieben wird. Diese tiefe Verwurzelung in der Landwirtschaft verbindet er mit seiner doppelten Expertise aus Mathematik und Finanzwirtschaft.",
      methodTitle: "Unsere Methode: Mehr als nur eine Datenbank",
      methodText: "Centeo nutzt einen mehrstufigen, KI-gestützten Prozess: Umfassende Datensammlung von über 555 Programmen, intelligentes Matching & Scoring für Ihre spezifische Situation, und strategische Analyse zur Maximierung Ihres Förderpotenzials."
    },
    es: {
      heroTitle: "Encuentra subvenciones UE",
      heroSubtitle: "En 3 minutos",
      heroDescription: "Buscamos en todos los programas de subvenciones UE disponibles y encontramos los adecuados para tu explotación.",
      priceText: isFreeMode ? "Temporalmente gratis" : "9,90€ una vez",
      ctaButton: isFreeMode ? "Empezar gratis" : "Empezar ahora",
      trustLine: "Desarrollado por agricultores para agricultores • Más de 555 programas • 30 días garantía de satisfacción",
      
      // Benefits Features Content
      benefitsTitle: "🚜 Finalmente todas las subvenciones a la vista",
      benefit1: "Desde 5.000€ pequeñas subvenciones hasta 2 millones€ grandes proyectos",
      benefit2: "También programas \"ocultos\" en energía e innovación",
      benefit3: "Perfecto para sucesión, conversión ecológica, digitalización",
      benefit4: "549 programas analizados",
      benefit5: "6 países cubiertos",
      benefit6: "30 días garantía de devolución del dinero",
      
      // Sobre Nosotros Content
      aboutTitle: "Sobre Nosotros",
      aboutSubtitle: "Nuestras raíces, su futuro: La historia detrás de Centeo",
      missionTitle: "Nuestra misión: Claridad en la jungla de subvenciones",
      missionText: "La financiación agrícola en la UE es un laberinto de solicitudes, regulaciones cambiantes e innumerables programas. Fundamos Centeo porque creemos que cada agricultor, independientemente del tamaño de la explotación o los conocimientos técnicos, merece un acceso justo y sencillo al apoyo que le corresponde.",
      originTitle: "Nuestro origen: Agricultura en el corazón, datos en la mente",
      originText: "Detrás de Centeo hay una historia personal. Nuestro fundador, Arsenio Longo, proviene de una familia que ha estado en la agricultura durante generaciones. Esta profunda conexión con la agricultura la combina con su doble experiencia en matemáticas y finanzas.",
      methodTitle: "Nuestro método: Más que solo una base de datos",
      methodText: "Centeo utiliza un proceso de múltiples etapas asistido por IA: Recopilación integral de datos de más de 555 programas, emparejamiento inteligente y puntuación para su situación específica, y análisis estratégico para maximizar su potencial de financiación."
    },
    it: {
      heroTitle: "Trova finanziamenti UE",
      heroSubtitle: "In 3 minuti",
      heroDescription: "Cerchiamo in tutti i programmi di finanziamento UE disponibili e troviamo quelli adatti per la tua azienda.",
      priceText: isFreeMode ? "Temporaneamente gratuito" : "9,90€ una volta",
      ctaButton: isFreeMode ? "Inizia gratis" : "Inizia ora",
      trustLine: "Sviluppato da agricoltori per agricoltori • Oltre 555 programmi • 30 giorni garanzia soddisfazione",
      
      // Benefits Features Content
      benefitsTitle: "🚜 Finalmente tutti i finanziamenti in vista",
      benefit1: "Da 5.000€ piccoli finanziamenti fino a 2 milioni€ grandi progetti",
      benefit2: "Anche programmi \"nascosti\" in energia e innovazione",
      benefit3: "Perfetto per successione, conversione bio, digitalizzazione",
      benefit4: "549 programmi analizzati",
      benefit5: "6 paesi coperti",
      benefit6: "30 giorni garanzia rimborso",
      
      // Chi Siamo Content
      aboutTitle: "Chi Siamo",
      aboutSubtitle: "Le nostre radici, il vostro futuro: La storia dietro Centeo",
      missionTitle: "La nostra missione: Chiarezza nella giungla dei finanziamenti",
      missionText: "Il finanziamento agricolo nell'UE è un labirinto di domande, regolamenti in continua evoluzione e innumerevoli programmi. Abbiamo fondato Centeo perché crediamo che ogni agricoltore, indipendentemente dalle dimensioni dell'azienda o dalle competenze tecniche, meriti un accesso equo e semplice al supporto che gli spetta.",
      originTitle: "La nostra origine: Agricoltura nel cuore, dati nella mente",
      originText: "Dietro Centeo c'è una storia personale. Il nostro fondatore, Arsenio Longo, proviene da una famiglia che è stata nell'agricoltura per generazioni. Questa profonda connessione con l'agricoltura la combina con la sua doppia competenza in matematica e finanza.",
      methodTitle: "Il nostro metodo: Più di un semplice database",
      methodText: "Centeo utilizza un processo multi-fase assistito dall'IA: Raccolta dati completa di oltre 555 programmi, abbinamento intelligente e punteggio per la vostra situazione specifica, e analisi strategica per massimizzare il vostro potenziale di finanziamento."
    }
  };
  
  const currentContent = content[lang] || content.de;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      {/* Scrollender Ticker Banner */}
      <div className="bg-green-700 text-white py-4 sm:py-5 overflow-hidden relative group">
        <div 
          className="whitespace-nowrap flex group-hover:pause ticker-animation"
        >
          <style>
            {`
              @keyframes scroll {
                0% { transform: translateX(0%); }
                100% { transform: translateX(-50%); }
              }
              .ticker-animation {
                animation: scroll 8s linear infinite;
              }
              @media (min-width: 768px) {
                .ticker-animation {
                  animation: scroll 15s linear infinite;
                }
              }
              .group:hover .group-hover\\:pause {
                animation-play-state: paused;
              }
            `}
          </style>
          <div className="flex-shrink-0 px-6 sm:px-10 text-base sm:text-lg lg:text-xl font-medium">
            {currentContent.benefit1} • {currentContent.benefit2} • {currentContent.benefit3} • {currentContent.benefit4} • {currentContent.benefit5} • {currentContent.benefit6}
          </div>
          <div className="flex-shrink-0 px-6 sm:px-10 text-base sm:text-lg lg:text-xl font-medium">
            {currentContent.benefit1} • {currentContent.benefit2} • {currentContent.benefit3} • {currentContent.benefit4} • {currentContent.benefit5} • {currentContent.benefit6}
          </div>
        </div>
      </div>

      {/* Durum Price Widget Section */}
      {showDurum && (
      <section className="py-8 bg-gradient-to-b from-amber-25 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                {lang === 'de' ? '🌾 Live Durum-Weizen Markt' :
                 lang === 'es' ? '🌾 Mercado de Trigo Duro en Vivo' :
                 '🌾 Mercato del Grano Duro in Tempo Reale'}
              </h2>
              <p className="text-sm text-gray-600">
                {lang === 'de' ? 'Aktuelle Preise und KI-Prognosen für europäische Durumweizen-Märkte' :
                 lang === 'es' ? 'Precios actuales y pronósticos de IA para los mercados europeos de trigo duro' :
                 'Prezzi attuali e previsioni AI per i mercati europei del grano duro'}
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Widget */}
              <div className="lg:col-span-2">
                <DurumWidget />
              </div>
              {/* Compact Widget */}
              <div className="space-y-4">
                <DurumWidget compact={true} />
                {/* Partner badges hidden in public mode */}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Hero Section - Vereinfacht */}
      <section className="relative py-12 lg:py-16">
        <div className="container mx-auto px-6 text-center max-w-4xl">
          {/* Logo */}
          <div className="mb-8">
            <img src="/CENTEO-C.png" alt="Centeo" className="h-32 sm:h-40 lg:h-48 mx-auto mb-6" />
          </div>
          
          {/* Hauptüberschrift - Mobile optimiert */}
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-gray-900 mb-4 leading-tight">
            {currentContent.heroTitle}
          </h1>
          <div className="text-xl sm:text-2xl lg:text-3xl text-green-700 font-semibold mb-6 sm:mb-8">
            {currentContent.heroSubtitle}
          </div>
          
          {/* Kurze Beschreibung - Mobile optimiert */}
          <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4 sm:px-0">
            {currentContent.heroDescription}
          </p>
          
          {/* Preis/Gratis-Hinweis */}
          <div className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 px-4 sm:px-0">
            {isFreeMode ? (
              <>
                {lang === 'de' ? 'Vorübergehend kostenlos – nur E-Mail erforderlich.' :
                 lang === 'es' ? 'Temporalmente gratis — solo email necesaria.' :
                 'Temporaneamente gratuito — basta l\'email.'}
              </>
            ) : (
              <>Vollständige Analyse für nur <span className="font-bold text-green-700">{currentContent.priceText}</span></>
            )}
          </div>
          
          {/* Hauptbutton - Mobile optimiert */}
          <button 
            onClick={onStart}
            className="bg-green-700 hover:bg-green-800 text-white px-8 sm:px-10 py-4 rounded-xl text-lg sm:text-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 mb-4 mx-4 sm:mx-0 w-auto"
          >
            {isFreeMode ? (
              lang === 'de' ? 'Kostenlos starten' : lang === 'es' ? 'Empezar gratis' : 'Inizia gratis'
            ) : (
              lang === 'de' ? 'Analyse starten für 9,90€' : lang === 'es' ? 'Iniciar análisis por 9,90€' : 'Inizia analisi per 9,90€'
            )} →
          </button>
          
          {/* Vertrauen direkt unter Button */}
          <div className="text-sm text-gray-600 mb-8">
            {lang === 'de' ? 'Kein Abo. 30 Tage Zufriedenheitsgarantie.' :
             lang === 'es' ? 'Sin suscripción. 30 días garantía de satisfacción.' :
             'Nessun abbonamento. 30 giorni garanzia soddisfazione.'}
          </div>
          
          {/* Early Access Banner - vorsichtig integriert */}
          <div className="max-w-2xl mx-auto px-4">
            <EarlyAccessBanner />
          </div>
          
          {/* Vertrauensindikatoren */}
          <div className="text-sm text-gray-500">
            ✓ {currentContent.trustLine}
          </div>
        </div>
      </section>


      {/* Über Uns Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{content[lang].aboutTitle}</h2>
            <h3 className="text-xl text-green-700 font-medium">{content[lang].aboutSubtitle}</h3>
          </div>
          
          {/* Mission Section */}
          <div className="mb-12 bg-green-50 rounded-lg p-8">
            <h4 className="text-xl font-semibold mb-4 text-gray-900">{content[lang].missionTitle}</h4>
            <p className="text-gray-700 leading-relaxed text-lg">{content[lang].missionText}</p>
          </div>

          {/* Origin Section */}
          <div className="mb-12 bg-blue-50 rounded-lg p-8">
            <h4 className="text-xl font-semibold mb-4 text-gray-900">{content[lang].originTitle}</h4>
            <p className="text-gray-700 leading-relaxed">{content[lang].originText}</p>
          </div>

          {/* Method Section */}
          <div className="mb-12 bg-purple-50 rounded-lg p-8">
            <h4 className="text-xl font-semibold mb-4 text-gray-900">{content[lang].methodTitle}</h4>
            <p className="text-gray-700 leading-relaxed">{content[lang].methodText}</p>
          </div>
        </div>
      </section>

      {/* Final CTA - Einfach */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-lg mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {lang === 'de' ? 'Bereit zu starten?' : lang === 'es' ? '¿Listo para empezar?' : 'Pronto per iniziare?'}
            </h2>
            
            <div className="space-y-4">
              <button 
                onClick={onStart}
                className="w-full bg-green-700 hover:bg-green-800 text-white px-10 py-4 rounded-xl text-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                {isFreeMode ? (
                  lang === 'de' ? 'Kostenlos starten' : lang === 'es' ? 'Empezar gratis' : 'Inizia gratis'
                ) : (
                  lang === 'de' ? 'Analyse starten' : lang === 'es' ? 'Iniciar análisis' : 'Inizia analisi'
                )} →
              </button>

            </div>
            
            <div className="mt-4 text-sm text-gray-600">
              {lang === 'de' ? 'Kein Abo. 30 Tage Zufriedenheitsgarantie.' :
               lang === 'es' ? 'Sin suscripción. 30 días garantía de satisfacción.' :
               'Nessun abbonamento. 30 giorni garanzia soddisfazione.'}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-100 border-t border-gray-200 py-6">
        <div className="container mx-auto px-6">
          <div className="text-center text-sm text-gray-600">
            © 2025 Centeo | 
            <button 
              onClick={onShowAboutUs}
              className="hover:text-gray-800 ml-2 bg-transparent border-none cursor-pointer underline"
            >
              {lang === 'de' ? 'Über Uns' : lang === 'es' ? 'Sobre Nosotros' : 'Chi Siamo'}
            </button> | 
            <button 
              onClick={() => setShowFAQ(true)}
              className="hover:text-gray-800 ml-2 bg-transparent border-none cursor-pointer underline"
            >
              {lang === 'de' ? 'FAQ' : lang === 'es' ? 'Preguntas Frecuentes' : 'Domande Frequenti'}
            </button> | 
            <button 
              onClick={() => setShowImpressum(true)}
              className="hover:text-gray-800 ml-2 bg-transparent border-none cursor-pointer underline"
            >
              Impressum
            </button> | 
            <button 
              onClick={() => setShowAGBDatenschutz(true)}
              className="hover:text-gray-800 ml-2 bg-transparent border-none cursor-pointer underline"
            >
              AGB & Datenschutz
            </button>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {showFAQ && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full h-full sm:h-auto sm:max-h-[95vh] overflow-y-auto relative">
            <button
              onClick={() => setShowFAQ(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold z-10"
            >
              ×
            </button>
            <FAQ />
          </div>
        </div>
      )}
      {showImpressum && (
        <Impressum onClose={() => setShowImpressum(false)} />
      )}
      {showAGBDatenschutz && (
        <AGBDatenschutz onClose={() => setShowAGBDatenschutz(false)} />
      )}
    </div>
  );
};

export default ImprovedHomePageClean;
